import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Card,
  CardBody,
  ExpandedIndex,
  HStack,
  Heading,
  Skeleton,
  Stack,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';

import { useListServices } from 'hooks/useApiQueries';
import { useCheckoutNavigate } from 'hooks/useCheckoutNavigate';
import { Service } from 'types/service';
import { ServicesProps } from '../../../types/services';
import { formatExpiration, toSlug } from '../../../utils/functions';

const cardBorderRadius = `${mondayMindbody?.customization?.cardBorderRadius}px`;
const primaryColor = mondayMindbody?.customization?.color ?? '#ff7f00';
const tryoutImage = mondayMindbody?.customization?.tryoutImage;

const brandProgramId = Number(mondayMindbody?.customization?.brandProgramId);
const brandName = mondayMindbody?.customization?.brandName;
// const studentProgramId = mondayMindbody?.customization?.studentProgramId; // Non utilisé car on y va en bourrin avec "étudiants" dans .Name

export function Services({ classId }: ServicesProps) {
  const isMobile = useBreakpointValue({ base: true, md: false });

  const { data, isLoading } = useListServices(classId);

  const services =
    data
      ?.filter((service) => service.ProgramId !== 27 && service.Price > 0)
      .sort((a, b) => {
        // Vérifie si le service contient "Routine"
        const isARoutine = /routine/i.test(a.Name);
        const isBRoutine = /routine/i.test(b.Name);

        // Priorisation des services contenant "Routine"
        if (isARoutine && !isBRoutine) return -1;
        if (!isARoutine && isBRoutine) return 1;

        // Vérifie si le service contient "Bienvenue", "Lancement" ou "Découverte"
        const isAWelcomingService = /Bienvenue|Lancement|Découverte/i.test(a.Name);
        const isBWelcomingService = /Bienvenue|Lancement|Découverte/i.test(b.Name);

        // Priorisation des services "Bienvenue", "Lancement" ou "Découverte"
        if (isAWelcomingService && !isBWelcomingService) return -1;
        if (!isAWelcomingService && isBWelcomingService) return 1;

        // Extraire les parties numériques initiales des noms, si elles existent
        const matchA = a.Name.match(/^\d+/);
        const matchB = b.Name.match(/^\d+/);
        const numA = matchA ? parseInt(matchA[0], 10) : NaN;
        const numB = matchB ? parseInt(matchB[0], 10) : NaN;

        // Si les deux éléments ont des préfixes numériques, les comparer
        if (!isNaN(numA) && !isNaN(numB)) {
          if (numA !== numB) return numA - numB;
          // Si les nombres sont égaux, comparer le reste de la chaîne
          return a.Name.localeCompare(b.Name);
        }

        // Si un seul élément a un préfixe numérique, il vient en premier
        if (!isNaN(numA) && isNaN(numB)) return -1;
        if (isNaN(numA) && !isNaN(numB)) return 1;

        // Si aucun des deux éléments n'a de préfixe numérique, effectuer un tri alphabétique
        return a.Name.localeCompare(b.Name);
      }) ?? [];

  const newClientServices = services?.filter(
    (service) => service.ProgramId !== 26 && service.IntroOfferType === 'NewAndExistingClients' && service.Price > 0,
  );
  // Filtrer les services pour Monday et Dynamo en plus de vérifier les nouveaux clients
  const mondayServices = services
    ?.filter(
      (service) =>
        service.ProgramId === 26 &&
        !service.Name.toLowerCase().includes('atelier') &&
        !service.Name.toLowerCase().includes('week-end') &&
        service.Price > 0,
    )
    .sort((a, b) => {
      // Prioriser "Offre de bienvenue 3 séances"
      if (/offre de bienvenue 3 séances/i.test(a.Name)) return -1;
      if (/offre de bienvenue 3 séances/i.test(b.Name)) return 1;

      // Prioriser "LE PACK D'ÉTÉ"
      if (/le pack d'été/i.test(a.Name)) return -1;
      if (/le pack d'été/i.test(b.Name)) return 1;

      // Prioriser "Offre de bienvenue Monday"
      if (/offre de bienvenue monday/i.test(a.Name)) return -1;
      if (/offre de bienvenue monday/i.test(b.Name)) return 1;

      // Trier les autres services en fonction des parties numériques de leur nom
      const matchA = a.Name.match(/^\d+/);
      const matchB = b.Name.match(/^\d+/);
      const numA = matchA ? parseInt(matchA[0], 10) : NaN;
      const numB = matchB ? parseInt(matchB[0], 10) : NaN;

      // Si les deux éléments ont des préfixes numériques, les comparer
      if (!isNaN(numA) && !isNaN(numB)) {
        if (numA !== numB) return numA - numB;
        // Si les nombres sont égaux, comparer le reste de la chaîne
        return a.Name.localeCompare(b.Name);
      }

      // Si un seul élément a un préfixe numérique, il vient en premier
      if (!isNaN(numA) && isNaN(numB)) return -1;
      if (isNaN(numA) && !isNaN(numB)) return 1;

      // Si aucun des deux éléments n'a de préfixe numérique, effectuer un tri alphabétique
      return a.Name.localeCompare(b.Name);
    });

  const brandServices = services
    ?.filter(
      (service) =>
        service.ProgramId === brandProgramId &&
        service.IntroOfferType !== 'NewClients' &&
        service.IntroOfferType !== 'NewAndExistingClients' &&
        !service.Name.toLowerCase().includes('stage') &&
        service.Price > 0,
    )
    .sort((a, b) => {
      // Prioriser "Challenge 5" avant "Challenge 10" et "Challenge 15"
      const aName = a.Name.toLowerCase();
      const bName = b.Name.toLowerCase();
      const challenge5A = aName.includes('challenge 5');
      const challenge5B = bName.includes('challenge 5');
      const challenge10A = aName.includes('challenge 10');
      const challenge10B = bName.includes('challenge 10');
      const challenge15A = aName.includes('challenge 15');
      const challenge15B = bName.includes('challenge 15');

      if (challenge5A && !challenge5B) return -1;
      if (!challenge5A && challenge5B) return 1;

      if (challenge10A && challenge10B) return 0; // Si les deux sont Challenge 10, gardez l'ordre original
      if (challenge10A && !challenge10B) return -1;
      if (!challenge10A && challenge10B) return 1;

      if (challenge15A && challenge15B) return 0; // Si les deux sont Challenge 15, gardez l'ordre original
      if (challenge15A && !challenge15B) return -1;
      if (!challenge15A && challenge15B) return 1;

      return a.Name.localeCompare(b.Name); // Tri alphabétique standard si aucune des règles ci-dessus ne s'applique
    });

  // Filter services for Riise if the siteId matches and name contains 'stage'
  const stageServices =
    data?.filter(
      (service) =>
        service.ProgramId ===
        ((mondayMindbody?.siteId === '5736555' && 40) ||
          (mondayMindbody?.siteId === '5736551' && 44) ||
          (mondayMindbody?.siteId === '5736526' && 42)), // Paradis Latin pour le site 5736526
    ) ?? [];

  // Identifier quels accordéons doivent être ouverts par défaut
  const defaultIndexMondayServices = mondayServices.length > 0 ? [0] : [];
  const defaultIndexBrandServices = brandServices.length > 0 ? [0] : [];
  const defaultIndexStageServices = stageServices.length > 0 ? [0] : [];
  const defaultIndexStudioServices: ExpandedIndex | undefined = []; // Fermé par défaut

  const studioServices = [
    {
      Id: 1,
      Name: '1 Séance Étudiant',
      Price: '21',
      ExpirationLength: 1,
      ExpirationUnit: 'month',
      Program: 'Etudiant',
      Count: 1,
      isNewClientOffer: false,
      isRiiseRetreat: false,
      Locations: 'dans les studios dynamo, RIISE et Punch',
    },
    {
      Id: 2,
      Name: '5 Séances Étudiant',
      Price: '95',
      ExpirationLength: 3,
      ExpirationUnit: 'months',
      Program: 'Étudiant',
      Count: 5,
      isNewClientOffer: false,
      isRiiseRetreat: false,
      Locations: 'dans les studios dynamo, RIISE et Punch',
    },
    {
      Id: 3,
      Name: '1 Back to Back',
      Price: '10',
      Description: "Uniquement disponible en studio après un premier cours s'il reste des spots pour le cours suivant",
      isNewClientOffer: false,
      isRiiseRetreat: false,
    },
  ];

  return (
    <Stack w="full" className="ServicesPage">
      <Heading className="ServicesTitle" textAlign="center" textTransform="uppercase" sx={{ margin: '1em 0' }}>
        Tarifs
      </Heading>
      <Box w="full" display="flex" flexWrap="wrap" flexFlow="row-wrap" justifyContent="center" alignItems={'center'}>
        {isLoading ? (
          <Loading />
        ) : (
          <>
            {newClientServices.map((service) => (
              <ServiceItem key={service.Id} service={service} isNewClientOffer={true} />
            ))}
            {/* Bannière pour les services mondayServices */}
            {mondayServices.length > 0 && (
              <>
                {isMobile ? (
                  <Accordion
                    allowToggle
                    display={{ base: 'block', md: 'none' }}
                    w="full"
                    defaultIndex={defaultIndexMondayServices}
                    className="mondayServicesAccordion"
                  >
                    <AccordionItem border="none">
                      <>
                        <h2>
                          <AccordionButton
                            p={4}
                            backgroundColor="rgba(217, 217, 217, 1)"
                            _expanded={{ bg: 'gray.200' }}
                          >
                            <Box flex="1" textAlign="center" fontWeight={600}>
                              Crédits Monday Sports Club
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                        </h2>
                        <AccordionPanel p={0}>
                          <Box
                            className="mondayServicesIntro"
                            w="full"
                            p="0"
                            fontSize="md"
                            fontWeight="bold"
                            textAlign="center"
                          >
                            Les crédits achetés sont valables dans tous les studios dynamo, Punch et RIISE.
                          </Box>
                          {mondayServices.map((service) => (
                            <Box key={service.Id} w="full" display="flex" justifyContent="center">
                              <ServiceItem
                                key={service.Id}
                                service={service}
                                isMondayServices={true}
                                isNewClientOffer={false}
                              />
                            </Box>
                          ))}
                        </AccordionPanel>
                      </>
                    </AccordionItem>
                  </Accordion>
                ) : (
                  <Box
                    id="mondayServicesContainer"
                    display="flex"
                    flexDirection="row"
                    flexFlow="row wrap"
                    alignItems="center"
                    justifyContent="space-between"
                    w="full"
                  >
                    <Box
                      w="full"
                      backgroundColor="rgba(217, 217, 217, 1)"
                      p="1em"
                      textTransform="uppercase"
                      textAlign="center"
                      mt={5}
                      mb={5}
                      fontWeight={600}
                      color="black"
                      className="ServicesDivider"
                    >
                      <Text className="ServicesDividerText" fontSize="xxl">
                        Crédits Monday Sports Club
                      </Text>
                    </Box>
                    <Box
                      className="mondayServicesIntro"
                      w="full"
                      p="0"
                      fontSize="md"
                      fontWeight="bold"
                      textAlign="center"
                    >
                      Les crédits achetés sont valables dans tous les studios dynamo, Punch et RIISE.
                    </Box>
                    {mondayServices.map((service) => (
                      <ServiceItem
                        key={service.Id}
                        service={service}
                        isNewClientOffer={false}
                        isMondayServices={true}
                      />
                    ))}
                  </Box>
                )}
              </>
            )}
            {/* Section brandServices avec accordéon pour mobile */}
            {brandServices.length > 0 && (
              <>
                {isMobile ? (
                  <Accordion
                    allowToggle
                    display={{ base: 'block', md: 'none' }}
                    w="full"
                    defaultIndex={defaultIndexBrandServices}
                    className="brandServicesAccordion"
                  >
                    <AccordionItem border="none">
                      <h2>
                        <AccordionButton p={4} backgroundColor="rgba(217, 217, 217, 1)" _expanded={{ bg: 'gray.200' }}>
                          <Box flex="1" textAlign="center" fontWeight={600}>
                            Crédits {brandName}
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                      </h2>

                      <AccordionPanel pb={4}>
                        {brandServices.map((service) => (
                          <Box key={service.Id} w="full" display="flex" justifyContent="center">
                            <ServiceItem
                              key={service.Id}
                              service={service}
                              isNewClientOffer={false}
                              isRiiseRetreat={false}
                            />
                          </Box>
                        ))}
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                ) : (
                  <Box
                    id="brandServicesContainer"
                    display="flex"
                    flexDirection="row"
                    flexFlow="row wrap"
                    alignItems="center"
                    justifyContent="space-between"
                    w="full"
                  >
                    <Box
                      w="full"
                      backgroundColor="rgba(217, 217, 217, 1)"
                      p="1em"
                      textTransform="uppercase"
                      textAlign="center"
                      mt={5}
                      mb={5}
                      fontWeight={600}
                      color="black"
                      className="ServicesDivider"
                    >
                      <Text className="ServicesDividerText" fontSize="xxl">
                        Crédits {brandName}
                      </Text>
                    </Box>
                    {brandServices.map((service) => (
                      <ServiceItem key={service.Id} service={service} isNewClientOffer={false} isRiiseRetreat={false} />
                    ))}
                  </Box>
                )}
              </>
            )}
            {/* Section stageServices avec accordéon pour mobile */}
            {stageServices.length > 0 && (
              <>
                {isMobile ? (
                  <Accordion
                    allowToggle
                    display={{ base: 'block', md: 'none' }}
                    w="full"
                    defaultIndex={defaultIndexStageServices}
                    className="stageServicesAccordion"
                  >
                    <AccordionItem border="none">
                      <h2>
                        <AccordionButton p={4} backgroundColor="rgba(217, 217, 217, 1)" _expanded={{ bg: 'gray.200' }}>
                          <Box flex="1" textAlign="center" fontWeight={600}>
                            {mondayMindbody?.siteId === '5736551'
                              ? 'Workshop'
                              : mondayMindbody?.siteId === '5736526'
                              ? 'PACK DYNAMO PARADIS LATIN - 17 JUIN'
                              : 'Les Stages'}
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                      </h2>

                      <AccordionPanel pb={4}>
                        {stageServices.map((service) => (
                          <Box key={service.Id} w="full" display="flex" justifyContent="center">
                            <ServiceItem
                              key={service.Id}
                              service={service}
                              isNewClientOffer={false}
                              isRiiseRetreat={true}
                            />
                          </Box>
                        ))}
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                ) : (
                  <Box
                    id="stageServicesContainer"
                    display="flex"
                    flexDirection="row"
                    flexFlow="row wrap"
                    alignItems="center"
                    w="full"
                    justifyContent="space-between"
                  >
                    <Box
                      w="full"
                      backgroundColor="rgba(217, 217, 217, 1)"
                      p="1em"
                      textTransform="uppercase"
                      textAlign="center"
                      mt={5}
                      mb={5}
                      fontWeight={600}
                      color="black"
                      className="ServicesDivider"
                    >
                      <Text className="ServicesDividerText" fontSize="xxl">
                        {mondayMindbody?.siteId === '5736551'
                          ? 'Workshop'
                          : mondayMindbody?.siteId === '5736526'
                          ? 'PACK DYNAMO PARADIS LATIN - 17 JUIN'
                          : 'Les Stages'}
                      </Text>
                    </Box>
                    {stageServices.map((service) => (
                      <ServiceItem key={service.Id} service={service} isNewClientOffer={false} isRiiseRetreat={true} />
                    ))}
                  </Box>
                )}
              </>
            )}
            {/* Services achetables en studio */}
            {studioServices.length > 0 && (
              <>
                {isMobile ? (
                  <Accordion
                    allowToggle
                    display={{ base: 'block', md: 'none' }}
                    w="full"
                    defaultIndex={defaultIndexStudioServices}
                    className="studioServicesAccordion"
                  >
                    <AccordionItem border="none">
                      <>
                        <h2>
                          <AccordionButton
                            p={4}
                            backgroundColor="rgba(217, 217, 217, 1)"
                            _expanded={{ bg: 'gray.200' }}
                          >
                            <Box flex="1" textAlign="center" fontWeight={600}>
                              Offres disponibles en studio
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                        </h2>
                        <AccordionPanel p={0}>
                          <Box w="full" p="0" fontSize="md" fontWeight="bold" textAlign="center">
                            Tarifs étudiants disponibles uniquement en studio sur présentation d'une carte étudiant en
                            cours de validité.
                          </Box>
                          <Box w="full" p="1em" fontSize="sm" textAlign="center">
                            Ces packs vous donneront accès à des sessions spécifiques signalées au planning par un *
                          </Box>

                          {studioServices.map((service) => (
                            <ServiceItem
                              key={service.Id}
                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                              // @ts-ignore
                              service={service}
                              isNewClientOffer={service.isNewClientOffer}
                              isRiiseRetreat={service.isRiiseRetreat}
                            />
                          ))}
                        </AccordionPanel>
                      </>
                    </AccordionItem>
                  </Accordion>
                ) : (
                  <Box
                    id="studioServicesMainContainer"
                    display="flex"
                    flexDirection="row"
                    flexFlow="row wrap"
                    alignItems="center"
                    justifyContent="center"
                    w="full"
                  >
                    <Box
                      w="full"
                      backgroundColor="rgba(217, 217, 217, 1)"
                      p="1em"
                      textTransform="uppercase"
                      textAlign="center"
                      mt={5}
                      mb={5}
                      fontWeight={600}
                      color="black"
                      className="ServicesDivider"
                    >
                      <Text className="ServicesDividerText" fontSize="xxl">
                        Offres disponibles en studio
                      </Text>
                    </Box>
                    <Box w="full" p="0" fontSize="md" fontWeight="bold" textAlign="center">
                      Tarifs étudiants disponibles uniquement en studio sur présentation d'une carte étudiant en cours
                      de validité.
                    </Box>
                    <Box w="full" p="1em" fontSize="sm" textAlign="center">
                      Ces packs vous donneront accès à des sessions spécifiques signalées au planning par un *
                    </Box>
                    <Box className="studioServicesContainer">
                      {studioServices.map((service) => (
                        <ServiceItem
                          key={service.Id}
                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                          // @ts-ignore
                          service={service}
                          isNewClientOffer={service.isNewClientOffer}
                          isRiiseRetreat={service.isRiiseRetreat}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Box>
    </Stack>
  );
}

interface ServiceItemProps {
  service: Service;
  isNewClientOffer?: boolean;
  isRiiseRetreat?: boolean;
  isMondayServices?: boolean;
}

function ServiceItem({
  service,
  isNewClientOffer = false,
  isRiiseRetreat = false,
  isMondayServices = false,
}: ServiceItemProps) {
  const onCheckout = useCheckoutNavigate({ ...service, type: 'service' });
  const isMobile = useBreakpointValue({ base: true, md: false });

  // On vérifie si le nom du service contient "étudiant"
  const isStudentOrPreBookingService =
    /étudiant|etudiant|pre|pré/i.test(service.Name) || // Check for "étudiant", "etudiant", "pre", "pré"
    !/stage/i.test(service.Name); // Check "back" as a whole word and exclude "stage"

  const isBackToBack = service.Name.toLowerCase().includes('back to back');

  const almaClientLayout = true;

  if (isNewClientOffer) {
    if (!almaClientLayout) {
      return (
        <Box
          display="flex"
          backgroundPosition="top center"
          backgroundSize="cover"
          backgroundImage={`url(${tryoutImage})`}
          backgroundColor="red"
          flexDirection={{ base: 'column', md: 'row' }}
          w="full"
          alignItems="center"
        >
          <Box borderRadius={cardBorderRadius} w="full" h="100%" className="serviceCard_NewClients-Container" />
          <Card
            className="serviceCard serviceCard_NewClients"
            cursor="pointer"
            flex={{ base: '1', md: '' }}
            flexGrow={{ base: '1', md: '' }}
            sx={{
              borderRadius: cardBorderRadius,
              backgroundColor: `${primaryColor} !important`,
              width: 'full',
              marginTop: { base: 4, md: 0 },
            }}
            onClick={onCheckout}
          >
            <ServiceBody service={service} isNewClientOffer={true} isRiiseRetreat={isRiiseRetreat} />
          </Card>
        </Box>
      );
    } else {
      return (
        <Box
          display="flex"
          justifyContent="space-between"
          flexDirection={{ base: 'column', md: 'row' }}
          alignItems="stretch"
          w={{ md: 'full' }}
        >
          <Card
            className="serviceCard serviceCard_NewClients"
            cursor="pointer"
            width="328px"
            sx={{
              borderRadius: cardBorderRadius,
              backgroundColor: `${primaryColor} !important`,
              marginTop: { base: 4, md: 0 },
            }}
            onClick={onCheckout}
          >
            <ServiceBody service={service} isNewClientOffer={true} isRiiseRetreat={isRiiseRetreat} />
          </Card>
          <img
            src={tryoutImage}
            alt="Tryout"
            style={{
              borderRadius: cardBorderRadius,
              width: '328px',
              height: '324px',
              objectFit: 'cover',
              marginTop: isMobile ? 16 : 0,
            }}
          />
          <Box
            className="almaTextContentContainer"
            width="328px"
            sx={{
              borderRadius: cardBorderRadius,
              border: `1px solid ${primaryColor} !important`,
              marginTop: { base: 4, md: 0 },
              marginBottom: { base: 4, md: 0 },
            }}
          >
            <Text className="almaTextContent almaTextContent--title">Nouveau</Text>
            <Text className="almaTextContent">
              N'attendez plus pour booker vos séances grâce au paiement en plusieurs fois
            </Text>
            <Text className="almaTextContent">
              <img
                src={`${import.meta.env.BASE_URL}almapayment.svg`}
                alt="Alma"
                style={{ height: '60px', width: 'auto' }}
              />
            </Text>
          </Box>
        </Box>
      );
    }
  }

  return (
    <Card
      className="serviceCard"
      w="full"
      cursor="pointer"
      sx={{
        marginLeft: isMobile ? 'auto' : undefined,
        marginRight: isMobile ? 'auto' : undefined,
        borderRadius: cardBorderRadius,
        backgroundColor: isNewClientOffer ? `${primaryColor} !important` : undefined,
      }}
      onClick={isStudentOrPreBookingService || isBackToBack ? undefined : onCheckout}
    >
      <ServiceBody
        service={service}
        isNewClientOffer={false}
        isRiiseRetreat={isRiiseRetreat}
        isMondayServices={isMondayServices}
      />
    </Card>
  );
}

function ServiceBody({
  service,
  isNewClientOffer = false,
  isRiiseRetreat = false,
}: {
  service: Service;
  isNewClientOffer: boolean;
  isRiiseRetreat: boolean;
  isMondayServices?: boolean;
}) {
  const pricePerSession = Math.round((service.Price / service.Count) * 2) / 2;

  const onCheckout = useCheckoutNavigate({ ...service, type: 'service' });

  // Vérifier si le nom du service contient "étudiant"
  const isStudentOrPreBookingService =
    service.Name.toLowerCase().includes('étudiant') ||
    service.Name.toLowerCase().includes('etudiant') ||
    service.Name.toLowerCase().includes('pre') ||
    service.Name.toLowerCase().includes('pré') ||
    (service.Name.toLowerCase().includes('back') && !service.Name.toLowerCase().includes('back to'));

  const isChallengeRoutine = service.Name.toLowerCase().includes('challenge');
  const isWelcomeService = service.Name.toLowerCase().includes('bienvenue');
  const isBackToBack = service.Name.toLowerCase().includes('back to back');

  return (
    <CardBody
      pointerEvents={isStudentOrPreBookingService ? 'none' : 'inherit'}
      className={`serviceCardBody serviceCardBody--${toSlug(service.Name)}`}
      display="flex"
      flexDirection="column"
      alignItems="center"
    >
      <Text
        className={isChallengeRoutine ? 'serviceChallenge serviceName' : 'serviceName'}
        height={isRiiseRetreat ? '125px' : '75px'}
        fontSize={isChallengeRoutine || isRiiseRetreat ? 'lg' : 'xxl'}
        fontWeight="bold"
        align="center"
      >
        {service.Name}
      </Text>
      {service.Count > 1 ? (
        <>
          <Text className="servicePrice" fontSize="xxl" align="center" fontWeight="bold">
            {pricePerSession}€/séance
          </Text>
          <Text className="servicePriceForAll" fontSize="lg" align="center" fontWeight="normal" mb={5}>
            soit {service.Price}€ les {service.Count} séances
          </Text>
        </>
      ) : (
        <>
          <Text className="servicePrice" fontSize="xxxl" align="center" fontWeight="bold">
            {service.Price}€
          </Text>
          <Text fontSize="lg" align="center" fontWeight="normal" mb={5}>
            &nbsp;
          </Text>
        </>
      )}
      <Text
        display={isRiiseRetreat ? 'none' : ''}
        fontSize={13}
        className="serviceExpiration serviceExpiration--line-one"
        align="center"
      >
        {isBackToBack && service.Description
          ? service.Description
          : /le pack d'été/i.test(service.Name)
          ? 'Valable du 15/07 au 30/09 dans les studios dynamo, RIISE et Punch'
          : !isChallengeRoutine
          ? `${formatExpiration(service.ExpirationLength, service.ExpirationUnit ?? '')}*`
          : // Condition pour les services "Challenge" avec gestion de l'id du site
          mondayMindbody?.siteId === '5736551'
          ? service.Name.toLowerCase().includes('challenge 5')
            ? 'Crédits valables uniquement chez Punch, du 15/06 au 15/07. À la clé, 1 crédit offert.'
            : service.Name.toLowerCase().includes('challenge 10')
            ? 'Crédits valables uniquement chez Punch, du 15/06 au 15/07. À la clé, 2 crédits offerts.'
            : service.Name.toLowerCase().includes('challenge 15')
            ? 'Crédits valables uniquement chez Punch, du 15/06 au 15/07. À la clé, 3 crédits offerts.'
            : ''
          : `Crédits valables uniquement chez ${brandName}, du 10/06 au 10/07. À la clé, ${
              service.Name.includes('DX15')
                ? '2 crédits offerts'
                : service.Name.includes('DX20')
                ? '2 crédits + 1 t-shirt offerts'
                : '1 crédit offert'
            }`}
      </Text>
      {service.Price > 100 && (
        <Text
          fontSize={14}
          align="center"
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
          className="serviceAlmaText"
        >
          Payez en plusieurs fois sans frais avec
          <img
            src={`${import.meta.env.BASE_URL}logoalmaorange.svg`}
            alt="Alma"
            className="serviceAlmaLogo"
            style={{
              height: 12,
              position: 'relative',
              top: 0,
              left: 3,
              width: 'auto',
            }}
          />
        </Text>
      )}
      <Text
        display={isRiiseRetreat ? 'none' : ''}
        fontSize={11}
        className="serviceExpiration serviceExpiration--line-two"
        align="center"
        sx={{
          marginBottom: 3,
          visibility: isChallengeRoutine || (!isWelcomeService && !isNewClientOffer) ? 'hidden' : 'visible',
        }}
      >
        Un seul achat par client et par expérience
      </Text>
      {(!isRiiseRetreat && !isStudentOrPreBookingService && !isBackToBack) || isRiiseRetreat ? (
        <Button
          sx={{
            marginBottom: 3,
            width: '103px',
            color: isNewClientOffer ? `${primaryColor} !important` : 'inherit',
          }}
          className="serviceCardButton"
          variant="contained"
          onClick={onCheckout}
          product-id={service.Id}
        >
          Acheter
        </Button>
      ) : (
        <Text fontSize="lg" align="center" fontWeight="bold" mb={3}>
          Achat en studio
        </Text>
      )}
    </CardBody>
  );
}

function Loading() {
  return (
    <HStack>
      {new Array(4).fill(null).map((_, index) => (
        <Card key={index}>
          <CardBody>
            <Stack>
              <Skeleton h="25px" w="90%" />
              <Skeleton h="28px" w="60%" />
              <Skeleton h="12px" w="80%" />
              <Skeleton h="12px" />
              <Skeleton h="40px" w="92px" borderRadius="md" />
            </Stack>
          </CardBody>
        </Card>
      ))}
    </HStack>
  );
}
